import React, { useCallback, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@cotera/client/v0/components/ui/resizable';
import type { ImperativePanelHandle } from 'react-resizable-panels';

/** Default size of the runs panel as a percentage when open. */
const RUNS_DEFAULT_SIZE = 30;
const RUNS_MIN_SIZE = 10;

/**
 * Collapsible & draggable "Runs" chrome for the workflow artifact editor: a
 * vertical `ResizablePanelGroup` wrapping `canvasSlot` (top) and the runs list
 * (bottom), so the user can drag the divider to resize.
 *
 * Horizontal at every width, deliberately. It used to become a column beside the
 * canvas whenever the artifact panel was expanded — which is exactly where the
 * node inspector docks in that same layout, leaving two panels contending for one
 * edge. Keeping runs along the bottom leaves that edge to the inspector, and means
 * both can be open at once: read a failed step, click its node, fix it.
 *
 * Uses `react-resizable-panels` (already in the project) so the user can drag the
 * divider to expand/shrink the panel beyond its default size.
 */
export const WorkflowEditorRunsPanel: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Content rendered in the top (canvas) panel. */
  canvasSlot?: React.ReactNode;
  children: React.ReactNode;
}> = ({ open, onOpenChange, canvasSlot, children }) => {
  const runsPanelRef = useRef<ImperativePanelHandle>(null);

  // Sync the panel's collapsed/expanded state with the `open` prop.
  useEffect(() => {
    const panel = runsPanelRef.current;
    if (panel === null) {
      return;
    }
    if (open) {
      if (panel.isCollapsed()) {
        panel.expand();
      }
    } else {
      if (!panel.isCollapsed()) {
        panel.collapse();
      }
    }
  }, [open]);

  // When the panel is collapsed/expanded via drag (past the collapse threshold),
  // notify the parent so the tab state stays in sync.
  const handleRunsPanelCollapse = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleRunsPanelExpand = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  const headerButton = (
    <button
      type="button"
      onClick={() => onOpenChange(!open)}
      aria-expanded={open}
      className="flex h-9 w-full shrink-0 cursor-pointer items-center justify-between gap-2 px-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/40"
    >
      <span>Runs</span>
      <ChevronDown
        className={cn(
          'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
          open ? 'rotate-0' : '-rotate-90'
        )}
      />
    </button>
  );

  // Closed: no resizable group at all, just the canvas above a slim bar.
  if (!open) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden">
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
          {canvasSlot}
        </div>
        <div className="shrink-0 border-t border-border bg-background">
          {headerButton}
        </div>
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="vertical" className="h-full w-full">
      <ResizablePanel
        id="workflow-canvas-panel"
        defaultSize={100 - RUNS_DEFAULT_SIZE}
        minSize={20}
        className="min-h-0 min-w-0 overflow-hidden"
      >
        {canvasSlot}
      </ResizablePanel>

      <ResizableHandle />

      <ResizablePanel
        ref={runsPanelRef}
        id="workflow-runs-panel"
        defaultSize={RUNS_DEFAULT_SIZE}
        minSize={RUNS_MIN_SIZE}
        collapsible
        collapsedSize={0}
        onCollapse={handleRunsPanelCollapse}
        onExpand={handleRunsPanelExpand}
        className="min-h-0 flex flex-col overflow-hidden bg-background border-t border-border shadow-[0_-8px_24px_-18px_rgba(15,23,42,0.45)]"
      >
        {headerButton}
        <div className="flex-1 min-h-0 overflow-hidden border-t border-border/60">
          {children}
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};
